/**
 * One `appUrlOpen` dispatcher for the whole app: quick actions, OAuth callbacks and deep links all arrive here.
 *
 * Why one: on a cold start the App plugin holds the launch URL until the *first* `appUrlOpen` listener attaches and
 * delivers it to that listener only. With several modules each adding their own listener, whichever attached
 * first got it and the rest never saw it. Here a single listener attaches at boot (main.ts), and handlers
 * subscribe:
 *
 *   const off = onAppUrlOpen((url) => isMine(url) ? (handle(url), true) : false)
 *
 * - Handlers run newest first (an OAuth flow waiting on its callback beats the generic deep-link handler); the
 *   first that returns `true` consumes the URL.
 * - A URL nobody consumed is kept for a few seconds and replayed to handlers that subscribe later, so a module
 *   that sets up after the launch event still gets it.
 * - The same URL is also returned by `App.getLaunchUrl()` on a cold start; it is read once and deduped against
 *   the event, so a quick action never runs twice.
 * - `getLaunchUrl()` isn't only a cold-start answer: iOS returns the last URL the app was opened with (warm opens
 *   too) and Android the launch intent's, for the life of the native process. A JS reload (Log out, impersonation,
 *   iOS reloading the WebView after its content process died) would replay it, so it's skipped when the page was
 *   reloaded, and ignored when this WebView session (sessionStorage) already dispatched that URL.
 */
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { isNativePlatform } from '@/utils/platform'

export type AppUrlHandler = (url: string) => boolean

/** The same URL arriving twice within this window (launch URL + event) is one open. */
export const APP_URL_DEDUPE_MS = 4000
/** How long an unclaimed URL waits for a handler to subscribe. */
export const APP_URL_REPLAY_MS = 15_000

interface Pending {
    url: string
    at: number
}

export interface AppUrlDispatcher {
    /** Deliver a URL (from the event or the launch URL). Returns whether a handler consumed it. */
    dispatch(url: string, now?: number): boolean
    subscribe(handler: AppUrlHandler, now?: number): () => void
}

/** The dispatching logic, without the plugin (unit-tested). */
export function createAppUrlDispatcher(): AppUrlDispatcher {
    const handlers: AppUrlHandler[] = []
    const recent = new Map<string, number>()
    let pending: Pending[] = []

    function run(url: string): boolean {
        for (const h of [...handlers].reverse()) {
            try {
                if (h(url)) return true
            } catch (err) {
                console.error('[appUrl] handler failed', err)
            }
        }
        return false
    }

    return {
        dispatch(url, now = Date.now()) {
            if (!url) return false
            for (const [u, at] of recent) if (now - at > APP_URL_DEDUPE_MS) recent.delete(u)
            if (recent.has(url)) return true
            recent.set(url, now)
            if (run(url)) return true
            pending = pending.filter((p) => now - p.at <= APP_URL_REPLAY_MS)
            pending.push({ url, at: now })
            return false
        },
        subscribe(handler, now = Date.now()) {
            handlers.push(handler)
            pending = pending.filter((p) => now - p.at <= APP_URL_REPLAY_MS)
            pending = pending.filter((p) => {
                try {
                    return !handler(p.url)
                } catch {
                    return true
                }
            })
            return () => {
                const i = handlers.indexOf(handler)
                if (i >= 0) handlers.splice(i, 1)
            }
        },
    }
}

const dispatcher = createAppUrlDispatcher()
let installed = false

/** URLs this WebView session already dispatched from the plugin. sessionStorage survives a reload, not a relaunch. */
const SEEN_KEY = 'lh:app-urls-seen'
const SEEN_MAX = 12

function seenUrls(): string[] {
    try {
        const list = JSON.parse(sessionStorage.getItem(SEEN_KEY) ?? '[]')
        return Array.isArray(list) ? list.filter((u): u is string => typeof u === 'string') : []
    } catch {
        return []
    }
}

function markSeen(url: string) {
    try {
        const list = seenUrls().filter((u) => u !== url)
        list.push(url)
        sessionStorage.setItem(SEEN_KEY, JSON.stringify(list.slice(-SEEN_MAX)))
    } catch {
        // Storage blocked: the reload check still applies.
    }
}

/** This page load is a reload (or history restore) of the app, not its first load in this WebView. */
function pageReloaded(): boolean {
    try {
        const nav = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined
        return nav?.type === 'reload' || nav?.type === 'back_forward'
    } catch {
        return false
    }
}

/** Subscribe to app URL opens (custom scheme, universal / app links, quick actions). Works before init too. */
export function onAppUrlOpen(handler: AppUrlHandler): () => void {
    return dispatcher.subscribe(handler)
}

/** Feed a URL through the dispatcher (dev lab, tests). */
export function dispatchAppUrl(url: string): boolean {
    return dispatcher.dispatch(url)
}

/** Attach the one plugin listener and read the cold-start launch URL. Call once, as early as possible. */
export async function initAppUrlDispatcher(): Promise<void> {
    if (installed || !isNativePlatform() || !Capacitor.isPluginAvailable('App')) return
    installed = true
    try {
        await App.addListener('appUrlOpen', ({ url }) => {
            if (url) markSeen(url)
            dispatcher.dispatch(url)
        })
    } catch {
        installed = false
        return
    }
    // A reload's launch URL is an old open (the event path already delivers a real cold start's).
    if (pageReloaded()) return
    try {
        const launch = await App.getLaunchUrl()
        if (!launch?.url || seenUrls().includes(launch.url)) return
        markSeen(launch.url)
        dispatcher.dispatch(launch.url)
    } catch {
        // Older shells, or no launch URL.
    }
}
